import type { RouteLocationRaw } from 'vue-router';

import { i18n } from '@/lang';
import { STORAGE_KEYS } from '@/platform/auth-runtime/token-storage';
import { isSafeNext } from '@/platform/routing/safe-next';
import { Path } from '@/platform/routing/targets';
import { reportNetworkError, sanitizeRequestData } from '@/platform/telemetry/frontend-alert';
import { axiosInstance } from '@/shared/lib/http/instance';
import { genRequestTicket } from '@/shared/lib/http/request-ticket';
import { WIRE_HEADERS } from '@/shared/lib/http/wire-headers';
import { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';

import type { AuthErrorPayload } from '@celerdata/host-bridge-contract';

// Re-export so existing consumers (which import axiosInstance from this
// module) keep working — only frontend-alert went direct to `./instance`
// to break the cycle.
export { axiosInstance };

// Shared HTTP client behavior:
// - baseURL via parsePrivateLink(env.VITE_BASE_API)
// - withCredentials true
// - 15 min timeout
// - Request interceptor: Accept-Language en-us + request-ticket headers
// - Response interceptor: code 20000 → unwrap data, else → reject + ElMessage
// - 429 → custom message (URL → mode lookup)
// - ignoreErrorMsg config opt-out
//
// Auth error handling:
// - code 50001 (host axios) and sub:auth-error bus events both funnel into
//   handleAuthError. Host owns the "session expired" alert + hard navigation
//   in both cases. resolveRedirect captures x-sr-aid BEFORE clearAuth wipes
//   it (spec §12 sequencing). The shared sign-out-lock guards concurrent
//   invocations across both axios and ws-bridge channels (resets in finally
//   so a future genuine auth event can re-trigger).
//
// Implementation note: v3 routes user-visible strings through vue-i18n so
// future locale work doesn't need to revisit this file.
//
// Deferred (TODO M4b later phases):
// - cluster_id injection for non-auth requests
// - octet-stream file download response
// - 50006 permission-list enrichment (needs allPermissions data load)
// - 40004 cluster-not-found eventBus pub

declare module 'axios' {
  export interface AxiosRequestConfig {
    ignoreErrorMsg?: boolean;
    // Opt-out for telemetry body capture: when true, requestData stays
    // out of the reportNetworkError payload. Use for endpoints whose
    // body is large, sensitive beyond the standard mask list, or pure
    // noise (file uploads, log dumps).
    errLogIgnoreReqBody?: boolean;
    // Per-request bookkeeping for the slow_network_request telemetry.
    // Stamped in the request interceptor, read in the response interceptor
    // to compute round-trip duration. Optional so existing AxiosRequestConfig
    // callers don't need to populate it.
    metadata?: { startTime?: number };
  }
}

// Anything slower than this threshold fires slow_network_request telemetry on
// success.
const SLOW_REQUEST_MS = 10_000;

type ServerEnvelope<T> = {
  code: number;
  message?: string;
  data?: T;
  mismatched_permissions?: Array<{ object: string; actions: string[] }>;
};

// A rejected non-success response. A real Error subclass so callers get both
// `instanceof ApiError` narrowing and the envelope fields (code / captcha /
// data) in one object.
export class ApiError extends Error {
  readonly code?: number;
  readonly captcha?: { site_key?: string };
  readonly data?: unknown;
  readonly mismatched_permissions?: Array<{ object: string; actions: string[] }>;
  constructor(
    message: string,
    envelope: {
      code?: number;
      captcha?: { site_key?: string };
      data?: unknown;
      mismatched_permissions?: Array<{ object: string; actions: string[] }>;
    },
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = envelope.code;
    this.captcha = envelope.captcha;
    this.data = envelope.data;
    this.mismatched_permissions = envelope.mismatched_permissions;
  }
}

const t = i18n.global.t;

let blockRequest = false;

axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // axios keeps config.url RELATIVE (baseURL is joined only at the adapter boundary), so the
    // telemetry endpoint is exactly '/log' — never '/api/log'. `===` rather than a suffix match
    // so a '/<domain>/log' endpoint can't also slip through the logout gate.
    if (blockRequest && config.url !== '/log') {
      // Deliberate gate (session expired / logout in progress) — tag the
      // rejection so the response error handler can report it under its
      // own type and the unhandledrejection handler can skip it.
      const err: Error & { manuallyBlocked?: boolean } = new Error('Request blocked.');
      err.manuallyBlocked = true;
      return Promise.reject(err);
    }
    config.headers.set('Accept-Language', 'en-us');

    const contentType = config.headers.get('Content-Type') as string | undefined;
    const isFormData = contentType?.includes('multipart/form-data');
    const bodyStr = isFormData || !config.data ? '' : JSON.stringify(config.data);
    // genRequestTicket is async because the signature uses native
    // SubtleCrypto.digest — axios v1 supports async interceptors natively.
    const ticket = await genRequestTicket(bodyStr);
    if (ticket) {
      Object.entries(ticket).forEach(([k, v]) => config.headers.set(k, v));
    }
    // Duration delta — Date.now() (local wall clock) is fine here; the
    // server-aligned clock matters for signature timestamps, not for
    // measuring elapsed time on this client.
    config.metadata = { ...config.metadata, startTime: Date.now() };
    return config;
  },
  (error) => {
    // Interceptor-time failures (signing, crypto.subtle unavailable, etc.)
    // fire `request_interceptor_error` so on-call sees them; otherwise the
    // request silently never sends.
    reportNetworkError('request_interceptor_error', error?.message ?? String(error), {
      stack: error?.stack,
    });
    return Promise.reject(error);
  },
);

// Each URL → mode key resolves to an i18n string; the outer template plugs
// `mode` into `request.recentRequest`.
const message429ModeKeyByUrl: Record<string, keyof typeof modeKeyMap> = {
  '/account/sign-up': 'signUpAccount',
  '/account/resend-sign-up': 'signUpAccount',
  '/account/reset-password': 'resetPassword',
  '/account/send-reset-password': 'resetPassword',
  '/account/find-accounts': 'findAccount',
};

const modeKeyMap = {
  signUpAccount: 'request.code429Mode.signUpAccount',
  resetPassword: 'request.code429Mode.resetPassword',
  findAccount: 'request.code429Mode.findAccount',
} as const;

const handleCode429Message = (url: string | undefined): string => {
  const modeKey = url ? message429ModeKeyByUrl[url] : undefined;
  if (modeKey) {
    return t('request.recentRequest', { mode: t(modeKeyMap[modeKey]) });
  }
  return t('request.tooManyRequests');
};

// Spec §5.1 / §12 — orchestrates host's auth-error flow for both the local
// 50001 axios branch and inbound sub:auth-error bus events.
//
// resolveRedirect / userVisiblePrompt are pure functions and exported so
// tests can pin them directly; handleAuthError owns the side effects and is
// exported so the sub:auth-error bus listener reuses the same code path.

import {
  beginSignOut,
  commitSignOut,
  endSignOut,
  isSignOutCommitted,
} from '@/platform/auth-runtime/sign-out-lock';

export function resolveRedirect(input?: AuthErrorPayload): RouteLocationRaw {
  // aid persists across logout (tokenStorage.clear keeps it), so the default
  // branch can prefill the account on the re-login screen.
  const aid = localStorage.getItem(STORAGE_KEYS.aid);

  // `next` post-login redirect is whitelisted to the OAuth consent path
  // (isSafeNext) — same gate the router guard and login consumers use.
  const rawNext = input?.next;
  const safeNext = isSafeNext(rawNext) ? rawNext : undefined;

  if (input?.reason === 'unsubscribe' || input?.reason === 're-signin') {
    return { path: Path.Login, query: { step: 'choose' } };
  }
  if (input?.reason === 'logout') {
    return safeNext
      ? { path: Path.Login, query: { next: safeNext } }
      : { path: Path.Login, query: { step: 'choose' } };
  }
  if (safeNext) {
    return { path: Path.Login, query: { next: safeNext } };
  }
  // A stored aid outranks the /mcp-authorize consent-return next: a
  // stored-account expiry re-logs in through the account-prefill step on every
  // route, and only a no-aid expiry on /mcp-authorize keeps the next.
  if (aid) {
    return { path: Path.Login, query: { step: 'user', aid } };
  }
  if (typeof window !== 'undefined' && window.location.pathname === Path.McpAuthorize) {
    return {
      path: Path.Login,
      query: { next: window.location.pathname + window.location.search },
    };
  }
  return { path: Path.Login, query: { step: 'choose' } };
}

export function userVisiblePrompt(
  input?: AuthErrorPayload,
): { title: string; content: string } | null {
  // Auth-error prompt paths:
  // - user-initiated logout / unsubscribe → no alert. The Logout button has its
  //   own "Are you sure?" sub-side confirm before reaching here, and the
  //   unsubscribe page messages the user inline.
  // - everything else (active 50001 + passive cross-tab re-signin) → reSignin.*
  //   ("Please sign in again / Login has timed out…").
  if (input?.reason === 'logout' || input?.reason === 'unsubscribe') return null;
  return { title: t('reSignin.title'), content: t('reSignin.desc') };
}

// Idempotent auth-error flow. Lazy-imports router + auth store to avoid the
// axios → store → axios circular module load (and so router can be stubbed in
// tests via vi.doMock without forcing eager binding here).
export async function handleAuthError(input?: AuthErrorPayload): Promise<void> {
  if (!beginSignOut()) {
    // A concurrent sign-out flow (WS forced-logout modal, cross-tab
    // storage listener) owns the lock. The 50001 interceptor already
    // flipped `blockRequest` synchronously before firing us — release
    // it here, or a user-cancel on the concurrent flow leaves the app
    // silently rejecting every outgoing request from the interceptor.
    // Skip the release once a shared sign-out flow has committed a nav:
    // the document is dying and unblocking would let queued requests fire
    // with cleared auth.
    if (!isSignOutCommitted()) blockRequest = false;
    return;
  }
  try {
    const target = resolveRedirect(input);
    const prompt = userVisiblePrompt(input);
    if (prompt) {
      await ElMessageBox.alert(prompt.content, prompt.title, {
        confirmButtonText: t('common.ok'),
        // Warning-shaped icon in the brand hue: `type` picks the icon, and
        // `sr-auth-dialog` (element.css) recolors it off EP's warning tint.
        type: 'warning',
        customClass: 'sr-auth-dialog',
        showClose: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
      }).catch(() => {
        // User dismissed the modal; redirect anyway.
      });
    }
    // Clear persisted auth BEFORE the hard-nav so the new page load
    // doesn't rehydrate a stale token. Do not blank the current Pinia
    // snapshot in this dying document; it can make old chrome briefly
    // render empty state before the browser commits the navigation.
    const { useAuthStore } = await import('@/platform/auth-runtime/store');
    useAuthStore().clearPersistedAuth();
    // Hard-nav on auth-context change (new token / perms / tenant): a full
    // reload deterministically resets every downstream store / cache /
    // singleton / websocket without the manual-invalidate tax on each
    // consumer. SaaS convention (GitHub / Slack / AWS all reload).
    const { router } = await import('@/platform/routing');
    commitSignOut();
    window.location.assign(router.resolve(target).fullPath);
    // blockRequest and the sign-out lock stay latched on purpose: the old
    // document lives until the hard-nav commits, and a late 50001 landing
    // in that window would re-enter here, stack a second session-expired
    // modal, and overwrite the aid/next target with step=choose. The new
    // document starts with fresh module state.
  } catch (e) {
    blockRequest = false;
    endSignOut();
    throw e;
  }
}

// Interceptor signature: axios's TS types expect the fulfilled handler to
// return `AxiosResponse | Promise<AxiosResponse>`, but this shared client
// unwraps to `res.data` (the inner payload) and callers downstream rely on
// that. Cast the function shape so TS accepts the unwrap.
const unwrappingFulfilled = (response: AxiosResponse<ServerEnvelope<unknown>>): unknown => {
  // Guard `response` itself: a null/undefined response (seen when reporting an
  // error through the telemetry path) must degrade to the "nothing to unwrap"
  // branch below rather than throwing a second error out of the interceptor.
  const res = response?.data;
  // Bail before the envelope check: reporting a /log response would itself POST /log.
  // Must read config.url — `request.responseURL` is the joined XHR url and is undefined under
  // any non-XHR adapter, which would silently disable this guard.
  if (response?.config?.url === '/log') {
    return undefined;
  }

  // Fires on success only — the network_request_error branch below covers
  // duration-bearing failures via a separate report path.
  const startTime = response?.config?.metadata?.startTime;
  if (typeof startTime === 'number') {
    const duration = Date.now() - startTime;
    if (duration > SLOW_REQUEST_MS) {
      reportNetworkError('slow_network_request', 'Slow network request', {
        url: response?.request?.responseURL,
        method: response?.config?.method,
        duration,
        status: response?.status,
        requestData: response.config.errLogIgnoreReqBody
          ? undefined
          : sanitizeRequestData(response?.config?.data),
      });
    }
  }

  // Binary downloads (CSV export, invoice/receipt PDF) don't carry a {code, data}
  // envelope; when content-type is octet-stream, hand back the raw body plus the
  // server-suggested name from the `file-name` header (legacy request.js read the
  // same header) so callers can name the saved file — dropping the header made
  // every export save as a bare timestamp. `responseType: 'blob'` makes `res` a Blob.
  const rawCt = response?.headers?.['content-type'];
  const contentType = typeof rawCt === 'string' ? rawCt : '';
  if (contentType.includes('application/octet-stream')) {
    const rawFileName = response?.headers?.['file-name'];
    const fileName = typeof rawFileName === 'string' ? rawFileName : '';
    return { res, fileName };
  }

  // Some responses (HTTP 204, aborted requests) leave response.data
  // undefined. Treat as a success with nothing to unwrap rather than letting
  // `res.code` throw a TypeError into the rejection handler.
  if (res === undefined || res === null) {
    return undefined;
  }

  // code 20000 = success. Anything else is a logical error even when HTTP
  // status is 200.
  if (res.code !== 20000) {
    const message = res.message || t('form.error');

    if (res.code === 50001) {
      blockRequest = true;
      void handleAuthError();
    } else {
      // 50001 has its own redirect path — don't double-report. Everything
      // else gets a telemetry event.
      reportNetworkError('api_response_error', res.message ?? '', {
        responseCode: res.code,
        responseMessage: res.message,
        url: response?.request?.responseURL,
        method: response?.config?.method,
        requestData: response.config.errLogIgnoreReqBody
          ? undefined
          : sanitizeRequestData(response?.config?.data),
        userBrowserId: response?.config?.headers?.[WIRE_HEADERS.ubid],
        httpStatus: response?.status,
      });
    }

    if (!response.config.ignoreErrorMsg) {
      ElMessage({ message, type: 'error', duration: 3000 });
    }

    return Promise.reject(new ApiError(message, res));
  }

  return res.data;
};

axiosInstance.interceptors.response.use(
  unwrappingFulfilled as unknown as (r: AxiosResponse) => AxiosResponse,
  (error) => {
    // Deliberate client-side gate (session expired / logout in progress), not a
    // failure — drop silently. The unhandledrejection handler also skips it.
    if (error?.manuallyBlocked) return Promise.reject(error);
    // Client-cancelled / aborted requests aren't failures — skip reporting and
    // tag so the unhandledrejection path skips them too.
    if (error?.code === 'ERR_CANCELED') {
      if (error && typeof error === 'object') {
        (error as { reportedByInterceptor?: boolean }).reportedByInterceptor = true;
      }
      return Promise.reject(error);
    }
    // Best-effort endpoints, so a failure is neither reported nor surfaced — reporting a failed
    // /log would itself POST /log. Both urls are relative; the joined form never appears here.
    if (error?.config?.url === '/time' || error?.config?.url === '/log') return undefined;

    // Capture network errors (timeout, DNS, 5xx, 429) unconditionally.
    reportNetworkError('network_request_error', error?.message ?? '', {
      url: error?.config?.url,
      method: error?.config?.method,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      errorMessage: error?.message,
      errorCode: error?.code,
      requestData: error?.config?.errLogIgnoreReqBody
        ? undefined
        : sanitizeRequestData(error?.config?.data),
      responseData: sanitizeRequestData(error?.response?.data),
    });
    // Tag so the unhandledrejection handler doesn't double-count this as
    // a generic promise_rejection on top of the network_request_error
    // already emitted above.
    if (error && typeof error === 'object') {
      (error as { reportedByInterceptor?: boolean }).reportedByInterceptor = true;
    }

    if (error?.response?.status === 429) {
      if (!error?.config?.ignoreErrorMsg) {
        ElMessage({
          message: handleCode429Message(error?.config?.url),
          type: 'error',
          duration: 3000,
        });
      }
      return Promise.reject(error);
    }
    if (error?.response?.data?.message && !error?.config?.ignoreErrorMsg) {
      ElMessage({
        message: error.response.data.message,
        type: 'error',
        duration: 3000,
      });
    } else if (!error?.config?.ignoreErrorMsg) {
      ElMessage({ message: t('common.serverError'), type: 'error', duration: 3000 });
    }
    return Promise.reject(error);
  },
);
